/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint } from "models/loaded";
import {
  StrategyDashboardConsumptionData,
  TimeAggregation,
  useGetStrategyDashboardConsumptionQuery,
} from "src/store/api/generatedApi";
import {
  ConsumptionTimeSeries,
  ConsumptionTimeSeriesKey,
  GroupedConsumptionSeries,
} from "../types";

export const useStrategyDashboardConsumption = (
  start?: string,
  end?: string,
  timeAggregation?: TimeAggregation
): Loaded<GroupedConsumptionSeries> => {
  const loadedRawData = loadedEndpoint(
    useGetStrategyDashboardConsumptionQuery({
      tenantName: useTenant(),
      start,
      end,
      timeAggregation,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: buildGroupedConsumptionSeries(loadedRawData.data),
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

const buildGroupedConsumptionSeries = (
  data: StrategyDashboardConsumptionData
): GroupedConsumptionSeries => {
  const stringKeyKeyMap: Record<string, ConsumptionTimeSeriesKey> = {};
  const stringKeyValuesMap: Record<string, ConsumptionTimeSeries> = {};

  for (const item of data.series) {
    const key: ConsumptionTimeSeriesKey = {
      consumptionSource: item.consumption_source,
      materialGrouping: item.material_grouping_name,
      materialGroup: item.material_group_name,
      productGrouping: item.product_grouping,
      productGroup: item.product_group_name,
    };
    const stringKey = JSON.stringify(key);
    stringKeyKeyMap[stringKey] = key;

    const value = stringKeyValuesMap[stringKey] ?? {
      date: [],
      consumed_mass: [],
      consumed_fraction: [],
    };
    value.date.push(item.consumption_date);
    value.consumed_mass.push(item.consumed_mass);
    value.consumed_fraction.push(item.consumed_fraction);
    stringKeyValuesMap[stringKey] = value;
  }

  const values: [ConsumptionTimeSeriesKey, ConsumptionTimeSeries][] =
    Object.entries(stringKeyKeyMap).map(([stringKey, key]) => [
      key,
      stringKeyValuesMap[stringKey]!,
    ]);

  const availableTimeAggregations = new Set<TimeAggregation>([
    "daily",
    "monthly",
  ]);
  const availableMaterialGroupings = data.material_groupings;
  const availableConsumptionSources = data.consumption_sources;
  const availableProductGroups = Object.values(data.product_groupings).flatMap(
    (groups) => groups
  );

  return {
    items: values,
    availableTimeAggregations,
    availableMaterialGroupings,
    availableConsumptionSources,
    availableProductGroups,
    updatedAt: Date.now(),
  };
};
